import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import FAQStyles from './FAQ.styled';
import Layout from '../../components/Layout/Layout';
import SEO from '../../components/SEO/SEO';
import Error from '../../components/Error/Error';
import Heading from '../../components/Heading/Heading';
import FAQSearch from '../../components/FAQSearch/FAQSearch';
import FAQCategories from '../../components/FAQCategories/FAQCategories';
import { getFaqs } from '../../services/faqs';
import { slugByLabel } from '../../util/faqCategories';
import useGTM from '../../hooks/useGTM';
import theme from '../../styles/theme';
import CTACardv2 from '../../components/CTACardv2/CTACardv2';

const parseSuggestions = suggestions =>
  suggestions.map(({ faqQuestion: label, slug: value, faqCategory }) => ({
    label,
    value: value.replace(
      'resources/faqs/',
      `resources/faqs/${slugByLabel(faqCategory?.[0])}/`
    ),
  }));

const parseCategories = categories =>
  categories.map(({ title, slug, faqs = [] }) => ({
    title,
    slug,
    faqs: faqs.map(({ faqQuestion, slug, faqCategory }) => ({
      title: faqQuestion,
      slug: slug.replace(
        'resources/faqs/',
        `resources/faqs/${slugByLabel(faqCategory?.[0])}/`
      ),
    })),
  }));

function FAQ({
  categories = [],
  suggestions: initialSuggestions = [],
  isLoading,
  error,
  history,
  className,
}) {
  if (error) {
    console.error(error);
    return (
      <Layout>
        <Error />
      </Layout>
    );
  }

  const [triggerGTMEvent] = useGTM();
  const [searchError, setSearchError] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setSuggestions(parseSuggestions(initialSuggestions));
  }, []);

  const handleChange = ({ value, currentTarget: { form } }) => {
    if (value.indexOf('/resources/faqs/') !== -1) {
      history.push(value);
      return;
    }

    setSearch(value);

    if (value.length >= 3 || !value) {
      const submit = form.querySelector('button[type="submit"]');

      setTimeout(() => {
        submit.click();
      }, 100);
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    e.stopPropagation();

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    setSearchTimeout(
      setTimeout(async () => {
        triggerGTMEvent({
          name: 'vmf.cta_click',
          location: 'FAQs',
          category: 'frequently_asked',
          action: 'search',
          label: 'faqs_search',
        })();

        setIsSearching(true);
        const _suggestions = await getFaqs('all', false, search);

        if (_suggestions.status !== 200 && _suggestions.status !== 204) {
          setSuggestions([]);
          setSearchError(_suggestions.statusText);
        } else {
          if (_suggestions.data.result) {
            setSuggestions(parseSuggestions(_suggestions.data.result));
            setSearchError(false);
          } else {
            setSuggestions([]);
            setSearchError('No results.');
          }
        }

        setIsSearching(false);
      }, 300)
    );
  };

  return (
    <Layout isLoading={isLoading} className={className}>
      <SEO
        title='Frequently Asked Questions About Mobile Home Loans'
        description='Find answers to common questions about Vanderblit Mortage and Finance, Inc. home financing. If you have any more questions, contact us! We are always happy to help you better understand this process.'
      />

      <Heading
        isAnimated={false}
        appearance='dark'
        background={theme.colors.blue10}
      >
        <h1>Frequently Asked Questions</h1>
        <FAQSearch
          search={search}
          suggestions={suggestions}
          onChange={handleChange}
          onSubmit={handleSubmit}
          isSearching={isSearching}
          error={searchError}
        />
      </Heading>

      <FAQCategories categories={parseCategories(categories)} />

      <CTACardv2
        preHeaderContent={'Have Questions?'}
        headerContent={`We're Here to Help`}
        buttonText={'Contact Us'}
        buttonUrl={'/contact-us/'}
        gtmClassName={'contact'}
        backgroundBottom={theme.colors.white}
        ctaIntro={
          <>
            <p>
              Existing customers <a href='tel:1-800-970-7250'>1-800-970-7250</a>
            </p>
            <p>
              New customers <a href='tel:1-866-701-0467'>1-866-701-0467</a>
            </p>
          </>
        }
      />
    </Layout>
  );
}

FAQ.getInitialProps = async ({}) => {
  if (typeof window === 'undefined') {
    try {
      const suggestions = await getFaqs('all', false);

      if (suggestions.status !== 200) {
        throw suggestions.statusText;
      }

      const yourMortgage = suggestions.data.result.filter(
        ({ faqCategory, faqTopThree }) =>
          faqCategory.indexOf('Your Mortgage') > -1 && faqTopThree
      );
      const preOwnedFinancing = suggestions.data.result.filter(
        ({ faqCategory, faqTopThree }) =>
          faqCategory.indexOf('Pre-owned Financing') > -1 && faqTopThree
      );
      const insurance = suggestions.data.result.filter(
        ({ faqCategory, faqTopThree }) =>
          faqCategory.indexOf('Insurance') > -1 && faqTopThree
      );
      const propertyTaxes = suggestions.data.result.filter(
        ({ faqCategory, faqTopThree }) =>
          faqCategory.indexOf('Property Taxes & Tax Forms') > -1 && faqTopThree
      );
      const payment = suggestions.data.result.filter(
        ({ faqCategory, faqTopThree }) =>
          faqCategory.indexOf('Payment') > -1 && faqTopThree
      );
      const yourHome = suggestions.data.result.filter(
        ({ faqCategory, faqTopThree }) =>
          faqCategory.indexOf('Your Home') > -1 && faqTopThree
      );
      const myVmfAccount = suggestions.data.result.filter(
        ({ faqCategory, faqTopThree }) =>
          faqCategory.indexOf('My VMF Account') > -1 && faqTopThree
      );

      return {
        suggestions: suggestions.data.result,
        categories: [
          {
            title: 'Your Mortgage',
            slug: '/resources/faqs/your-mortgage/',
            faqs: yourMortgage || [],
          },
          {
            title: 'Insurance',
            slug: '/resources/faqs/insurance/',
            faqs: insurance || [],
          },
          {
            title: 'Property Taxes & Tax Forms',
            slug: '/resources/faqs/property-taxes-forms/',
            faqs: propertyTaxes || [],
          },
          {
            title: 'Payment',
            slug: '/resources/faqs/payment/',
            faqs: payment || [],
          },
          {
            title: 'Your Home',
            slug: '/resources/faqs/your-home/',
            faqs: yourHome || [],
          },
          {
            title: 'My VMF Account',
            slug: '/resources/faqs/my-vmf-account/',
            faqs: myVmfAccount || [],
          },
          {
            title: 'Pre-owned Financing',
            slug: '/resources/faqs/pre-owned-financing/',
            faqs: preOwnedFinancing || [],
          },
        ],
      };
    } catch (error) {
      return {
        error,
      };
    }
  } else {
    window.location.reload();
  }
};

FAQ.propTypes = {
  match: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  data: PropTypes.shape({}),
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
};

export default FAQStyles(FAQ);
